import { T, pinStatusLine, tt, usePinDrop, type LatLng, type ZoneOutline } from '@agos/shared'
import { Spinner, cn } from '@agos/ui'
import { ChevronDown, MapPinned } from 'lucide-react'
import { Suspense, lazy, useState } from 'react'

/**
 * *Mapa* under the address on *Nová objednávka* - FRONTEND.md §9.694, the owner's #13.
 *
 * *"... aby keď vytváraš novú obj aby ti aj zobrazilo mapu a ukázalo adresu kam to ide a aj sa bude dať
 * špendlíkom behať po mape a tým aj meniť adresu"*. It shows the point the address resolved to and lets
 * the operator move it; a drop keeps the pin's position, re-resolves the zone, and replaces the address
 * text when the place has a name (`pin-drop.ts` holds the rules).
 *
 * **Collapsed until it is opened, then remembered on this device.** The till's height is §9.114's and
 * §9.267's argument - a map nobody asked for would push the menu below the fold on every delivery - and
 * an e2e run, which starts with empty storage, loads no map tiles it did not ask for.
 *
 * **Leaflet is loaded with the map, not with the page**: `@agos/ui/map` is imported lazily, so opening the
 * builder for a table order never waits for it.
 */
const PinMap = lazy(() => import('@agos/ui/map').then((module) => ({ default: module.PinMap })))

const OPEN_KEY = 'agos.order-builder.map-open'

/** Storage that throws - a private window, a blocked site - is a closed map, not a broken page. */
function rememberedOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1'
  } catch {
    return false
  }
}

function rememberOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0')
  } catch {
    // The choice then lasts this visit, which is all a device that refuses storage can have.
  }
}

export interface DeliveryPinMapProps {
  /** Where the delivery goes, as the builder holds it. */
  point: LatLng | null
  /** The franchise's depot, where the map opens with no point yet. */
  depot: LatLng | null
  /** The pin was put down - the builder keeps this point and re-resolves the zone from it. */
  onPinMoved: (point: LatLng) => void
  /** A provider named the place under the pin - the builder writes it into the address box. */
  onPinAddress: (address: string) => void
  /**
   * The franchise's delivery zones, drawn under the pin to look at - §9.802, owner O149. `zoneOutlinesOf`'s answer,
   * empty where external couriers deliver. Required, so a caller cannot leave them off without saying so.
   */
  zones: readonly ZoneOutline[]
}

export function DeliveryPinMap({ point, depot, onPinMoved, onPinAddress, zones }: DeliveryPinMapProps) {
  const [open, setOpen] = useState(rememberedOpen)
  // §9.705 - the drop, its one reverse lookup and the late-answer check are `@agos/shared`'s `usePinDrop`,
  // because the customer's checkout draws the same map and must not come to disagree with this one.
  const { status, drop } = usePinDrop({ point, onPinMoved, onPinAddress })

  const toggle = () => {
    const next = !open
    setOpen(next)
    rememberOpen(next)
  }

  return (
    <div className="mt-2" data-delivery-map-panel>
      <button
        type="button"
        data-delivery-map-toggle
        aria-expanded={open}
        onClick={toggle}
        className="flex items-center gap-1.5 text-xs text-primary font-medium px-2.5 py-1 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
      >
        <MapPinned className="w-3.5 h-3.5" />
        <T>Mapa</T>
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          <div
            className="h-56 sm:h-64 rounded-xl overflow-hidden border border-border"
            data-delivery-map
          >
            <Suspense
              fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <PinMap point={point} depot={depot} onDrop={drop} pinLabel={tt('Miesto doručenia')} zones={zones} />
            </Suspense>
          </div>
          <p className="text-xs text-muted-foreground" role="status" data-delivery-map-status={status}>
            {pinStatusLine(status, point !== null)}
          </p>
        </div>
      )}
    </div>
  )
}
